import type { GraphEntityType } from '@api/api/ontology';
import { generatePath } from '@helpers/utils/paths/paths';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { isHighlighted, NonLinearStepperProps } from './NonLinearStepper';
import styles from './NonLinearStepperRevamp.module.scss';
import { NonLinearStepRevamp } from './non-linear-step-revamp/NonLinearStepRevamp';

export type NonLinearStepperRevampProps = NonLinearStepperProps & {
  graphNode?: { id: string; type: GraphEntityType };
};

export const NonLinearStepperRevamp = ({
  steps,
  activeIndex,
  iconSize = 32,
  graphNode,
}: NonLinearStepperRevampProps) => {
  const { t } = useTranslation(['widget']);
  const [indexErrorMap, setIndexErrorMap] = useState<{
    [key: number]: boolean;
  }>({});

  if (!steps.length) {
    return null;
  }

  const isSimpleVersion = steps.length === 2;

  const stepsContent = steps.map((step, index) => {
    const { shouldHighlightStep } = isHighlighted({
      isSimpleVersion: step.isSimple ?? isSimpleVersion,
      activeIndex,
      index,
      stepsLength: steps.length,
    });

    return (
      <NonLinearStepRevamp
        key={`step-${index}`}
        index={index}
        iconSize={iconSize}
        step={step}
        activeIndex={activeIndex}
        shouldHighlightStep={shouldHighlightStep}
        indexErrorMap={indexErrorMap}
        setIndexErrorMap={setIndexErrorMap}
      />
    );
  });

  const cardClassName = clsx(
    styles.stepAndConnectorWrapper,
    graphNode && styles.link
  );

  const openGraphLabel = t('widget:graphExplore.actions.openGraph');

  const card = graphNode ? (
    <Link
      to={generatePath({
        basePath: appPaths.graph,
        params: {
          [queryKeys.centralNode]: graphNode.id,
          [queryKeys.nodeType]: graphNode.type,
        },
      })}
      className={cardClassName}
      aria-label={openGraphLabel}
    >
      {stepsContent}
    </Link>
  ) : (
    <div className={cardClassName}>{stepsContent}</div>
  );

  return (
    <div className={styles.container}>
      {graphNode ? (
        <TooltipContainer label={openGraphLabel} placement="top">
          {card}
        </TooltipContainer>
      ) : (
        card
      )}
    </div>
  );
};
